import type { UseQueryResult } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useAgentsAndTimeslotsQuery } from '@/features/appointments/api/getAppointmentSlots';
import type { AgentInfo } from '@/features/appointments/utils/AgentInfo';
import {
  type NearbyAgentInfo,
  useNearbyAgentsQuery,
} from '@/features/scrollFlow/pages/Calendar/api/getNearbyAgents';
import { getClubCode, getZipCode } from '@/lib/tagging/utils';
import useAppointmentStore, {
  isInBranchAppointment as isInBranchSelector,
  isOnlineAppointment as isOnlineSelector,
} from '@/store/AppointmentStore';
import useFallbackWorkTypeId from './useFallbackWorkTypeId';

export type ResolvedAgentsQueryResult =
  | UseQueryResult<AgentInfo[], Error>
  | UseQueryResult<NearbyAgentInfo[], Error>;

const useResolvedAgentsQuery = (): ResolvedAgentsQueryResult => {
  const isInBranch = useAppointmentStore(isInBranchSelector);
  const isOnline = useAppointmentStore(isOnlineSelector);
  const selectedDestination = useAppointmentStore(
    (state) => state.selectedDestination,
  );
  const preSelectedZipCode = useAppointmentStore(
    (state) => state.preSelectedZipCode,
  );
  const agentSearchLocation = useAppointmentStore(
    (state) => state.agentSearchLocation,
  );

  const clubCode = useMemo(() => getClubCode(), []);
  const zipCode = preSelectedZipCode || getZipCode();
  const fallbackWorkTypeId = useFallbackWorkTypeId();

  const coordinateParams = agentSearchLocation
    ? { lat: agentSearchLocation.lat, lng: agentSearchLocation.lng }
    : { zipCode };

  const onlinePrimaryQuery = useAgentsAndTimeslotsQuery(
    {
      workTypeId: selectedDestination.workTypeId,
      clubCode,
      inBranch: false,
    },
    {
      enabled: selectedDestination.workTypeId !== '' && isOnline,
    },
  );

  const inBranchPrimaryQuery = useNearbyAgentsQuery(
    {
      workTypeId: selectedDestination.workTypeId,
      ...coordinateParams,
    },
    {
      enabled:
        selectedDestination.workTypeId !== '' &&
        isInBranch &&
        (!!agentSearchLocation || zipCode !== ''),
    },
  );

  const primaryQuery = isInBranch ? inBranchPrimaryQuery : onlinePrimaryQuery;

  const shouldUseFallback =
    primaryQuery.isSuccess && primaryQuery.data.length === 0;

  const onlineFallbackQuery = useAgentsAndTimeslotsQuery(
    {
      workTypeId: fallbackWorkTypeId,
      clubCode,
      inBranch: false,
    },
    {
      enabled: shouldUseFallback && isOnline && fallbackWorkTypeId !== '',
    },
  );

  const inBranchFallbackQuery = useNearbyAgentsQuery(
    {
      workTypeId: fallbackWorkTypeId,
      ...coordinateParams,
    },
    {
      enabled:
        shouldUseFallback &&
        isInBranch &&
        fallbackWorkTypeId !== '' &&
        (!!agentSearchLocation || zipCode !== ''),
    },
  );

  const fallbackQuery = isInBranch
    ? inBranchFallbackQuery
    : onlineFallbackQuery;

  return shouldUseFallback ? fallbackQuery : primaryQuery;
};

export default useResolvedAgentsQuery;
